import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  Maximize2,
  Pause,
  Play,
  Upload,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import type { Film, Preview } from "../shared/types";
import { formatTime } from "./api";
import { prefersReducedMotion, Resolve } from "./fx";
import {
  bilingual,
  LANG_NAMES,
  LANGS,
  locale,
  setLang,
  text,
  useLang,
  useText,
} from "./i18n";

const TEXT = bilingual({
  en: {
    modal: { close: "Close dialog" },
    drop: "Drop to add",
    player: {
      play: "Play video",
      pause: "Pause video",
      mute: "Mute video",
      unmute: "Unmute video",
      fullScreen: "Full screen",
      position: "Video position",
      failed: "This video could not load.",
      retry: "Try again",
    },
    loading: "Opening your studio",
    time: { now: "Now", yesterday: "Yesterday" },
  },
  es: {
    modal: { close: "Cerrar diálogo" },
    drop: "Suelta para añadir",
    player: {
      play: "Reproducir vídeo",
      pause: "Pausar vídeo",
      mute: "Silenciar vídeo",
      unmute: "Activar sonido",
      fullScreen: "Pantalla completa",
      position: "Posición del vídeo",
      failed: "No se ha podido cargar este vídeo.",
      retry: "Reintentar",
    },
    loading: "Abriendo tu estudio",
    time: { now: "Ahora", yesterday: "Ayer" },
  },
});

export const BRAND = "Foco";

/** Two channels, one sharp lens. `converged` draws the focused, white state. */
export function Mark({
  className = "",
  converged = false,
}: {
  className?: string;
  converged?: boolean;
}) {
  const id = useId().replace(/:/g, "");
  return (
    <svg
      className={`mark ${converged ? "conv" : ""} ${className}`}
      viewBox="0 0 48 48"
      aria-hidden="true"
    >
      <defs>
        <clipPath id={`${id}l`}>
          <circle cx="21" cy="21" r="13" />
        </clipPath>
        <clipPath id={`${id}k`}>
          <circle cx="34.9" cy="13.1" r="4.2" />
        </clipPath>
      </defs>
      <circle className="rc" cx="21" cy="21" r="16" strokeWidth="2.4" />
      <circle className="rr" cx="27" cy="27" r="16" strokeWidth="2.4" />
      <circle
        className="rc"
        cx="21"
        cy="21"
        r="16"
        strokeWidth="2.4"
        clipPath={`url(#${id}k)`}
      />
      <circle className="ln" cx="27" cy="27" r="13" clipPath={`url(#${id}l)`} />
    </svg>
  );
}
export function Logo({ className = "" }: { className?: string }) {
  return (
    <span className={`logo ${className}`} role="img" aria-label={BRAND}>
      <Mark />
      <span className="wordmark" aria-hidden="true">
        foco
      </span>
    </span>
  );
}
/** English or Spanish by their codes; each names its language for screen readers. */
export function LanguageSwitch({ className = "" }: { className?: string }) {
  const current = useLang();
  return (
    <div
      className={`seg lang-switch ${className}`}
      role="group"
      aria-label="Language · Idioma"
    >
      {LANGS.map((l) => (
        <button
          key={l}
          type="button"
          lang={l}
          aria-label={LANG_NAMES[l]}
          aria-pressed={current === l}
          onClick={() => setLang(l)}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
export function Ring({ className = "" }: { className?: string }) {
  return (
    <span className={`ring live ${className}`} aria-hidden="true">
      <svg viewBox="0 0 36 36">
        <circle className="tr" cx="18" cy="18" r="15.9" />
        <circle className="pr" cx="18" cy="18" r="15.9" pathLength="100" />
      </svg>
    </span>
  );
}
export function Button({
  children,
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={`button ${className}`} {...props}>
      {children}
    </button>
  );
}
export function IconButton({
  label,
  children,
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      className={`icon-button ${className}`}
      aria-label={label}
      title={label}
      {...props}
    >
      {children}
    </button>
  );
}
export function Modal({
  title,
  subtitle,
  children,
  onClose,
  wide = false,
  className = "",
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  className?: string;
}) {
  const t = useText(TEXT).modal;
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      aria-label={title}
      className={`modal ${wide ? "modal-wide" : ""} ${className}`}
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <span className="modal-grab" aria-hidden="true" />
      <div className="modal-heading">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <IconButton label={t.close} onClick={onClose}>
          <X size={18} />
        </IconButton>
      </div>
      <div className="modal-body">{children}</div>
    </dialog>
  );
}

export function useMedia(query: string) {
  const [match, setMatch] = useState(() => matchMedia(query).matches);
  useEffect(() => {
    const list = matchMedia(query),
      sync = () => setMatch(list.matches);
    sync();
    list.addEventListener("change", sync);
    return () => list.removeEventListener("change", sync);
  }, [query]);
  return match;
}

/** Files dragged anywhere over the window; a drop anywhere hands them over. */
export function useFileDrop(
  onFiles: (files: FileList) => void,
  enabled = true,
) {
  const [over, setOver] = useState(false);
  const handler = useRef(onFiles);
  handler.current = onFiles;
  useEffect(() => {
    if (!enabled) return;
    let depth = 0;
    const files = (e: DragEvent) => !!e.dataTransfer?.types.includes("Files");
    const enter = (e: DragEvent) => {
      if (!files(e)) return;
      depth++;
      setOver(true);
    };
    const leave = (e: DragEvent) => {
      if (!files(e)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) setOver(false);
    };
    const hover = (e: DragEvent) => {
      if (!files(e)) return;
      e.preventDefault();
      e.dataTransfer!.dropEffect = "copy";
    };
    const drop = (e: DragEvent) => {
      if (!files(e)) return;
      e.preventDefault();
      depth = 0;
      setOver(false);
      if (e.dataTransfer?.files.length) handler.current(e.dataTransfer.files);
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragleave", leave);
    window.addEventListener("dragover", hover);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("dragover", hover);
      window.removeEventListener("drop", drop);
      setOver(false);
    };
  }, [enabled]);
  return over;
}
/** Closes a popup on a press outside it or Escape. */
export function useDismiss(
  ref: React.RefObject<HTMLElement | null>,
  open: boolean,
  close: () => void,
) {
  const handler = useRef(close);
  handler.current = close;
  useEffect(() => {
    if (!open) return;
    const press = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) handler.current();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") handler.current();
    };
    document.addEventListener("pointerdown", press);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", press);
      document.removeEventListener("keydown", key);
    };
  }, [open, ref]);
}
export function DropVeil({ label }: { label?: string }) {
  const fallback = useText(TEXT).drop;
  return (
    <div className="drop-veil" aria-hidden="true">
      <span className="drop-veil-icon">
        <Upload size={20} />
      </span>
      <b>{label ?? fallback}</b>
    </div>
  );
}

/** Plays the ready moment once: static drains and the split converges. */
export function useResolveOnArrival(
  canvas: React.RefObject<HTMLCanvasElement | null>,
  poster: string,
  arrived: boolean,
) {
  const [resolving, setResolving] = useState(
    arrived && !prefersReducedMotion(),
  );
  useEffect(() => {
    if (!resolving || !canvas.current) return;
    let cancelled = false;
    const r = new Resolve(canvas.current);
    const image = new Image();
    image.src = poster;
    r.src = image;
    r.set(0.4);
    const run = async () => {
      if (!image.complete) await image.decode().catch(() => undefined);
      if (cancelled) return;
      r.draw(0, true);
      await r.lock(900);
      if (!cancelled) setResolving(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [resolving, canvas, poster]);
  return resolving;
}

/**
 * A film playing live from its preview build, before any MP4 exists. It runs
 * from the separate preview origin, so the Director's code never shares the
 * studio's origin.
 */
export function PreviewPlayer({
  preview,
  fresh = false,
  compact = false,
}: {
  preview: Preview;
  fresh?: boolean;
  compact?: boolean;
}) {
  return (
    <div className={`player ${compact ? "player-compact" : ""}`}>
      <div className={`phone ${fresh ? "landed" : ""}`}>
        <div className="screen">
          <iframe
            className="preview-frame"
            src={preview.poster ? `${preview.url}#cover` : preview.url}
            title={preview.title}
            sandbox="allow-scripts allow-same-origin"
            allow="autoplay; fullscreen"
            allowFullScreen
            referrerPolicy="no-referrer"
          />
        </div>
      </div>
    </div>
  );
}

export function FilmPlayer({
  film,
  fresh = false,
  compact = false,
}: {
  film: Film;
  fresh?: boolean;
  compact?: boolean;
}) {
  const t = useText(TEXT).player;
  const ref = useRef<HTMLVideoElement>(null),
    frame = useRef<HTMLDivElement>(null),
    fx = useRef<HTMLCanvasElement>(null);
  const [playing, setPlaying] = useState(false),
    [time, setTime] = useState(0),
    [muted, setMuted] = useState(false),
    [error, setError] = useState(false);
  const resolving = useResolveOnArrival(fx, film.poster, fresh);
  useEffect(() => {
    setPlaying(false);
    setTime(0);
    setError(false);
  }, [film.id]);
  const toggle = () => {
    if (ref.current?.paused)
      void ref.current.play().catch(() => setError(true));
    else ref.current?.pause();
  };
  const progress = (time / (film.duration || 1)) * 100;
  return (
    <div className={`player ${compact ? "player-compact" : ""}`}>
      <div
        className={`phone ${resolving ? "resolving" : ""} ${fresh && !resolving ? "landed" : ""}`}
        ref={frame}
      >
        <div className="screen">
          <video
            ref={ref}
            key={film.id}
            src={film.url}
            poster={film.poster}
            preload="metadata"
            playsInline
            muted={muted}
            onTimeUpdate={() => setTime(ref.current?.currentTime || 0)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onError={() => setError(true)}
            onClick={toggle}
            aria-label={film.title}
          />
          {resolving && <canvas ref={fx} className="fx" aria-hidden="true" />}
          {!playing && !error && !resolving && (
            <button className="big-play" onClick={toggle} aria-label={t.play}>
              <Play size={22} fill="currentColor" />
            </button>
          )}
          {error && (
            <div className="player-error">
              <span>{t.failed}</span>
              <button
                className="button ghost"
                onClick={() => {
                  setError(false);
                  ref.current?.load();
                }}
              >
                {t.retry}
              </button>
            </div>
          )}
        </div>
        <span className="edge" aria-hidden="true" />
      </div>
      <div className="controls">
        <input
          className="scrub"
          type="range"
          aria-label={t.position}
          min="0"
          max={film.duration || 1}
          step="0.1"
          value={time}
          onChange={(e) => {
            if (ref.current) ref.current.currentTime = Number(e.target.value);
            setTime(Number(e.target.value));
          }}
          style={{ "--progress": `${progress}%` } as React.CSSProperties}
        />
        <div className="controls-row">
          <IconButton
            className="control-play"
            label={playing ? t.pause : t.play}
            onClick={toggle}
          >
            {playing ? (
              <Pause size={15} fill="currentColor" />
            ) : (
              <Play size={15} fill="currentColor" />
            )}
          </IconButton>
          <span className="timecode mono">
            <b>{formatTime(time)}</b> / {formatTime(film.duration)}
          </span>
          <IconButton
            label={muted ? t.unmute : t.mute}
            onClick={() => setMuted(!muted)}
          >
            {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
          </IconButton>
          <IconButton
            label={t.fullScreen}
            onClick={() =>
              void frame.current?.requestFullscreen().catch(() => {})
            }
          >
            <Maximize2 size={16} />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
export function Loading() {
  const t = useText(TEXT);
  return (
    <div className="loading" role="status" aria-label={t.loading}>
      <span className="loading-mark">
        <Mark />
      </span>
    </div>
  );
}

export function relativeTime(iso: string) {
  const t = text(TEXT).time;
  const then = new Date(iso),
    seconds = (Date.now() - then.getTime()) / 1000;
  if (seconds < 90) return t.now;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h`;
  const days = Math.floor(seconds / 86400);
  if (days === 1) return t.yesterday;
  if (days < 7) return then.toLocaleDateString(locale(), { weekday: "short" });
  return then.toLocaleDateString(locale(), { day: "numeric", month: "short" });
}
export function ProductTile({ name }: { name: string }) {
  return (
    <span className="fav" aria-hidden="true">
      {(name.trim()[0] || "·").toUpperCase()}
    </span>
  );
}
